import React, { useEffect } from 'react'
import styled from 'styled-components'
import PublicPageLayout from '../../components/PublicPageLayout'

const LegalWrapper = styled.div`
  background-color: #ffffff;
  background-image: 
    linear-gradient(#f8fafc 1px, transparent 1px),
    linear-gradient(90deg, #f8fafc 1px, transparent 1px);
  background-size: 30px 30px;
  min-height: calc(100vh - 72px);
  padding: 100px 24px;
  display: flex;
  justify-content: center;
  scroll-behavior: smooth;
`

const LegalContainer = styled.div`
  max-width: 1200px;
  width: 100%;
  display: flex;
  gap: 80px;
  @media (max-width: 992px) {
    flex-direction: column;
    gap: 40px;
  }
`

const Sidebar = styled.aside`
  width: 280px;
  flex-shrink: 0;
  position: sticky;
  top: 120px;
  height: fit-content;
  @media (max-width: 992px) {
    position: static;
    width: 100%;
  }
`

const PageTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  color: hsl(210, 55%, 22%);
  margin-bottom: 40px;
  letter-spacing: -2px;
`

const NavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const NavItem = styled.li`
  a {
    text-decoration: none;
    font-size: 14px;
    font-weight: ${props => props.active ? '700' : '500'};
    color: ${props => props.active ? 'hsl(33, 100%, 50%)' : '#94a3b8'};
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;

    &::before {
      content: '';
      width: ${props => props.active ? '8px' : '0px'};
      height: 8px;
      background: hsl(33, 100%, 50%);
      border-radius: 50%;
      transition: all 0.3s ease;
    }

    &:hover {
      color: hsl(33, 100%, 50%);
    }
  }
`

const ContentArea = styled.main`
  flex: 1;
`

const Section = styled.section`
  margin-bottom: 80px;
  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #111;
    margin-bottom: 24px;
  }
  p {
    font-size: 1.1rem;
    line-height: 1.8;
    color: #555;
    max-width: 700px;
  }
`

const LegalLayout = ({ title, navItems, children }) => {
    const [activeSection, setActiveSection] = React.useState(navItems[0].id)

    useEffect(() => {
        window.scrollTo(0, 0)

        const observerOptions = {
            root: null,
            rootMargin: '-20% 0px -70% 0px',
            threshold: 0
        }

        const handleIntersect = (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    setActiveSection(entry.target.id)
                }
            })
        }

        const observer = new IntersectionObserver(handleIntersect, observerOptions)

        // Observe all sections
        navItems.forEach((item) => {
            const el = document.getElementById(item.id)
            if (el) observer.observe(el)
        })

        return () => observer.disconnect()
    }, [navItems])

    return (
        <PublicPageLayout>
            <LegalWrapper>
                <LegalContainer>
                    <Sidebar>
                        <PageTitle>{title}</PageTitle>
                        <NavList>
                            {navItems.map((item, idx) => (
                                <NavItem key={idx} active={activeSection === item.id}>
                                    <a href={`#${item.id}`}>{item.label}</a>
                                </NavItem>
                            ))}
                        </NavList>
                    </Sidebar>
                    <ContentArea>
                        {children}
                    </ContentArea>
                </LegalContainer>
            </LegalWrapper>
        </PublicPageLayout>
    )
}

export const PrivacyPage = () => {
    const nav = [
        { id: 'introduction', label: 'Protocol Introduction' },
        { id: 'governance', label: 'Data Governance' },
        { id: 'auth', label: 'Authentication & Privacy' },
        { id: 'security', label: 'Financial Integrity' },
        { id: 'retention', label: 'Legacy & Retention' }
    ]

    return (
        <LegalLayout title="Privacy" navItems={nav}>
            <Section id="introduction">
                <h2>Protocol Introduction</h2>
                <p>
                    At Wise Academy Rwanda, we operate with a "Privacy by Design" philosophy. This document outlines
                    our rigorous standards for handling the digital identity and intellectual progress of our
                    community members. Our commitment is to maintain absolute transparency in how your data
                    powers your educational journey.
                </p>
            </Section>

            <Section id="governance">
                <h2>Data Governance</h2>
                <p>
                    We implement strict data minimization protocols. Wise Academy only processes the essential
                    metadata required to architect a secure learning environment: verified legal names,
                    institutional email addresses, and regional contact information for secure processing via
                    our partner, PawaPay. We maintain a zero-commercialization policy regarding user data.
                </p>
            </Section>

            <Section id="auth">
                <h2>Authentication & Privacy</h2>
                <p>
                    Our integration with Google and GitHub OAuth is restricted to identity verification and
                    session management. Wise Academy does not request, nor do we possess the capability to
                    access, private repositories, personal files, or social interactions. Your external
                    accounts remain entirely siloed from our internal academic databases.
                </p>
            </Section>

            <Section id="security">
                <h2>Financial Integrity</h2>
                <p>
                    All financial interactions for certification and advanced tracks are encrypted and handled
                    exclusively by PawaPay. Wise Academy utilizes industry-standard tokenization, ensuring
                    that sensitive credentials, including mobile money PINs and banking details, are never
                    stored or visible on our local infrastructure.
                </p>
            </Section>

            <Section id="retention">
                <h2>Legacy & Retention</h2>
                <p>
                    Your educational achievements are part of your professional legacy. We retain academic
                    records for the lifetime of your account to ensure your certifications remain verifiable
                    by future employers. Personal metadata can be purged upon formal account decommissioning,
                    subject to regulatory compliance.
                </p>
            </Section>
        </LegalLayout>
    )
}

export const TermsPage = () => {
    const nav = [
        { id: 'framework', label: 'Regulatory Framework' },
        { id: 'usage', label: 'Code of Conduct' },
        { id: 'intellectual', label: 'Proprietary Content' },
        { id: 'economic', label: 'Economic Policies' },
        { id: 'disruption', label: 'Service Continuity' }
    ]

    return (
        <LegalLayout title="Terms" navItems={nav}>
            <Section id="framework">
                <h2>Regulatory Framework</h2>
                <p>
                    By accessing Wise Academy, you enter into a binding agreement to adhere to our institutional
                    standards and the laws of the Republic of Rwanda. These terms govern the digital relationship
                    between the Academy and its global community of students and instructors.
                </p>
            </Section>

            <Section id="usage">
                <h2>Code of Conduct</h2>
                <p>
                    Wise Academy is a space for accelerated intellectual growth. Acceptable usage excludes any
                    form of automated data harvesting, security circumvention, or professional misconduct.
                    Violation of this digital ethical code will result in immediate revocation of access
                    without prior notification.
                </p>
            </Section>

            <Section id="intellectual">
                <h2>Proprietary Content</h2>
                <p>
                    Every module, video transcript, and assessment framework hosted here constitutes protected
                    intellectual property. Users are granted a limited, non-exclusive license for personal
                    advancement. Reproduction, redistribution, or unauthorized commercial exploitation of
                    Academy material is strictly prohibited.
                </p>
            </Section>

            <Section id="economic">
                <h2>Economic Policies</h2>
                <p>
                    While our core mission emphasizes free institutional access, premium tracks and certifications
                    are subject to our Economic Policy. Refund requests for paid certifications are eligible
                    for review within a 7-day window, contingent on the assessment status remaining "uninitiated."
                </p>
            </Section>

            <Section id="disruption">
                <h2>Service Continuity</h2>
                <p>
                    Wise Academy reserves the right to suspend or decommission accounts that compromise platform
                    stability or violate our terms. We prioritize service continuity for the community and
                    will intervene only to protect the integrity of the collective learning experience.
                </p>
            </Section>
        </LegalLayout>
    )
}
